import { datePageSearchParamsSchema, ipDatePagingSearchParamsSchema } from "@core/model/searchParams";
import { diffDays } from "@core/utils/etcUtil";
import z from "zod";

// **** 로더 정의 ****
export const datePageParam100DaysValidateLoader = createValidateLoader(
  datePageSearchParamsSchema
    .refine(refineStartBeforeEnd, {
      message: "startDate는 endDate보다 빠르거나 같아야 합니다.",
      path: ["startDate"],
    })
    .refine(refineDateRange100Days, {
      message: "날짜 범위는 100일을 초과할 수 없습니다.!",
      path: ["startDate"],
    })
    .refine(refineAllOrNoneSelect(["startDate", "endDate", "page"]), {
      message: "모두 함께 있어야 합니다.",
      path: ["startDate"], // path 아무거나 하나만 찍자
    }),
  ["startDate", "endDate", "page", "dateRadioValue"],
);

export const datePageParamValidateLoader = createValidateLoader(
  datePageSearchParamsSchema
    .refine(refineStartBeforeEnd, {
      message: "startDate는 endDate보다 빠르거나 같아야 합니다.",
      path: ["startDate"],
    })
    .refine(refineAllOrNoneSelect(["startDate", "endDate", "page"]), {
      message: "모두 함께 있어야 합니다.",
      path: ["startDate"], // path 아무거나 하나만 찍자
    }),
  ["startDate", "endDate", "page", "dateRadioValue"],
);

export const ipDatePagingParamValidateLoader = createValidateLoader(
  ipDatePagingSearchParamsSchema
    .refine(refineStartBeforeEnd, {
      message: "startDate는 endDate보다 빠르거나 같아야 헙니다.",
      path: ["startDate"],
    })
    .refine(refineAllOrNoneSelect(["page", "startDate", "endDate"]), {
      message: "모두 함께 있어야 합니다.",
      path: ["startDate"], // path 아무거나 하나만 찍자
    }),
  ["ip", "page", "startDate", "endDate", "dateRadioValue"],
);

// **** 유틸 함수 정의 ****

/**
 * 검색 파라미터 유효성 검사 로더 생성
 * @param schema 타겟 스키마
 * @param keys 타겟 파라미터 키
 * @returns 검색 파라미터 유효성 검사 로더
 */
function createValidateLoader(schema: z.ZodTypeAny, keys: string[]) {
  return async ({ request }: { request: Request }) => {
    const url = new URL(request.url);

    const rawParams = keys.reduce(
      (acc, key) => {
        const value = url.searchParams.get(key);
        // 안 넣으면 undefined가 됨, 무작정 다 넣으면 null이 됨
        if (value !== null) acc[key] = value;
        return acc;
      },
      {} as Record<string, string | null>,
    );

    const result = schema.safeParse(rawParams);

    if (result.error) {
      console.error(result.error?.issues.map((issue) => issue.message).join("\n"));
    }

    // 검색 파라미터 유효성 검사 실패 시 리다이렉트
    if (!result.success) {
      // console.error(result.error.issues.map((issue) => issue.message).join("\n"));
      // return redirect(url.pathname);

      // 히스토리 리플레이스 하기. redirect로 하면 하나가 추가된다.
      window.history.replaceState(null, "", url.pathname);
      window.dispatchEvent(new PopStateEvent("popstate"));
    }

    return null;
  };
}

/**
 * 시작일이 종료일보다 이전인지 validation
 */

function refineStartBeforeEnd<T extends { startDate?: string; endDate?: string }>(data: T) {
  if (!data.startDate || !data.endDate) return true;
  return new Date(data.startDate) <= new Date(data.endDate);
}

/**
 * 모두 같이 있는지 validation
 */
function refineAllOrNone(data: Record<string, unknown>) {
  const values = Object.values(data);
  const anySet = values.some((v) => v !== null && v !== undefined && v !== "");
  const allSet = values.every((v) => v !== null && v !== undefined && v !== "");
  return !anySet || allSet;
}

/**
 * 지정한 키가 모두 있는지 validation
 */
function refineAllOrNoneSelect(keys: string[]) {
  return (data: Record<string, unknown>) => {
    const values = keys.map((key) => data[key]);
    const anySet = values.some((v) => v !== null && v !== undefined && v !== "");
    const allSet = values.every((v) => v !== null && v !== undefined && v !== "");
    return !anySet || allSet;
  };
}

/**
 * 날짜 범위가 100일 이하인지 validation
 */
function refineDateRange100Days<T extends { startDate?: string; endDate?: string }>(data: T) {
  if (!data.startDate || !data.endDate) return true;
  return diffDays(data.startDate, data.endDate) <= 100;
}